// Archive search for the browser. The publish step writes a compact entry per
// published story into public documents: meta/searchIndex, plus
// meta/searchIndex-1 … -N when it outgrows one document. The client loads them
// once per session (1 read per shard) and ranks matches locally. No search service.
import { CONTENT_TYPE_LABELS, TOPIC_LABELS, type ContentType, type Story, type Topic } from "./model.ts";
import { FLAG_LABELS, effectiveFlag, type FlagStatus } from "./flags.ts";
import { PRODUCT_BY_ID } from "./products.ts";

export interface SearchEntry {
  id: string;
  t: string; // title
  g: string[]; // topic tags
  p: string[]; // product IDs
  tp: Topic;
  ty: ContentType;
  d: number; // publishedAt ?? sortAt
  f: FlagStatus | null;
  s: string; // source name
  x: boolean; // sample
  r: boolean; // official excerpt, no editor summary
}

export interface SearchIndexDoc {
  generatedAt: number;
  entries: SearchEntry[];
  truncated: boolean;
}

/** Firestore documents max out at 1 MiB; each shard stays well under it. */
export const SEARCH_INDEX_MAX_BYTES = 850_000;
/** About 3,000 stories per shard; 8 shards ≈ 24,000 stories. */
export const SEARCH_INDEX_MAX_SHARDS = 8;

export function toSearchEntry(s: Story): SearchEntry {
  return {
    id: s.id,
    t: s.title,
    g: s.topicTags.slice(0, 8),
    p: s.products,
    tp: s.primaryTopic,
    ty: s.contentType,
    d: s.publishedAt ?? s.sortAt,
    f: effectiveFlag(s)?.status ?? null,
    s: s.sourceName,
    x: s.isSample,
    r: s.summaryProvenance === "source_excerpt",
  };
}

/** Newest first; drops the oldest entries if they wouldn't fit in `maxShards` shards. */
export function buildSearchIndex(published: Story[], now = Date.now(), maxShards = SEARCH_INDEX_MAX_SHARDS): SearchIndexDoc {
  const entries = [...published].sort((a, b) => b.sortAt - a.sortAt || a.id.localeCompare(b.id)).map(toSearchEntry);
  const shards = splitSearchShards(entries);
  const kept = shards.slice(0, maxShards).flat();
  return { generatedAt: now, entries: kept, truncated: kept.length < entries.length };
}

/** Splits entries (in order) into shards that each serialize under the size budget. */
export function splitSearchShards(entries: SearchEntry[], maxBytes = SEARCH_INDEX_MAX_BYTES): SearchEntry[][] {
  const shards: SearchEntry[][] = [];
  let cur: SearchEntry[] = [];
  let bytes = 2; // "[]"
  for (const e of entries) {
    const n = JSON.stringify(e).length + 1;
    if (cur.length && bytes + n > maxBytes) {
      shards.push(cur);
      cur = [];
      bytes = 2;
    }
    cur.push(e);
    bytes += n;
  }
  if (cur.length) shards.push(cur);
  return shards;
}

export function normalizeText(s: string): string {
  return s.normalize("NFKD").replace(/[̀-ͯ]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
}

interface Prepared {
  e: SearchEntry;
  title: string;
  titleWords: string[];
  meta: string[]; // tag/product/topic/type/source/flag words
}

export function prepare(entries: SearchEntry[]): Prepared[] {
  return entries.map((e) => {
    const title = normalizeText(e.t);
    const metaText = [
      ...e.g,
      ...e.p.flatMap((id) => [id, PRODUCT_BY_ID.get(id)?.name ?? ""]),
      TOPIC_LABELS[e.tp] ?? "",
      CONTENT_TYPE_LABELS[e.ty] ?? "",
      e.s,
      e.f ? FLAG_LABELS[e.f] : "",
      e.f === "deprecated" ? "deprecated deprecation" : "",
      e.f === "ga" ? "generally available" : "",
      e.f === "public_preview" || e.f === "private_preview" ? "preview" : "",
    ].join(" ");
    return { e, title, titleWords: title.split(" "), meta: normalizeText(metaText).split(" ").filter(Boolean) };
  });
}

export interface SearchHit {
  entry: SearchEntry;
  score: number;
}

/** Every term must match somewhere. Title matches outrank metadata; newer breaks ties. */
export function search(prepared: Prepared[], query: string, limit = 50, now = Date.now()): SearchHit[] {
  const terms = normalizeText(query).split(" ").filter(Boolean);
  if (!terms.length) return [];
  const hits: SearchHit[] = [];
  for (const p of prepared) {
    let score = 0;
    let ok = true;
    for (const term of terms) {
      let best = 0;
      for (const w of p.titleWords) {
        if (w === term) best = Math.max(best, 4);
        else if (w.startsWith(term)) best = Math.max(best, 3);
      }
      if (best < 3 && term.length >= 3 && p.title.includes(term)) best = Math.max(best, 1.5);
      for (const w of p.meta) {
        if (w === term) best = Math.max(best, 2.5);
        else if (w.startsWith(term)) best = Math.max(best, 2);
      }
      if (!best) {
        ok = false;
        break;
      }
      score += best;
    }
    if (!ok) continue;
    if (p.title.includes(terms.join(" ")) && terms.length > 1) score += 2; // phrase bonus
    const ageDays = Math.max(0, (now - p.e.d) / 86_400_000);
    score += Math.max(0, 1 - ageDays / 365); // recency, at most +1
    hits.push({ entry: p.e, score });
  }
  return hits.sort((a, b) => b.score - a.score || b.entry.d - a.entry.d).slice(0, limit);
}
